import {
  ptacHostItem,
  ptacUnitRoomId,
  ptacUnitZone,
  ptacUnitZoneItems
} from '@alpinehomeair/common';
import {
  CondenserType,
  ConfigurationType,
  ExposureDirectionType,
  GridSpaceDataType,
  LevelType,
  PositionType,
  SpaceType,
  ZoneItemType,
  ZoneType
} from 'data/types';
import { nanoid } from 'nanoid';
import { calculateHeightOffGround, renumberZones } from 'utils/configurations';
import { collectDuctedLayoutSpaces } from 'utils/ducted-layout-configuration';
import { getGridSpacesForLevel } from 'utils/grid';
import {
  PtacConfigurationSyncParams,
  PtacLevelContextLookup,
  PtacRoom,
  PtacRoomLookup,
  PtacUnitIds
} from 'utils/ptac-configuration-types';
import { ptacLevelContextFor } from 'utils/ptac-level-context';
import {
  defaultPtacUnitPlacement,
  snapPtacUnitToRoom
} from 'utils/ptac-unit-placement';
import { PtacLevelContext } from 'utils/ptac-unit-placement-types';

export const PTAC_EMPTY_UNIT_NAME = 'PTAC unit';

// One unit per conditioned room the diagram can draw (the whole-house ducted
// precedent's room set).
export function collectPtacRooms(structure: LevelType[]): PtacRoom[] {
  return collectDuctedLayoutSpaces(structure);
}

export function ptacRoomLookup(structure: LevelType[]): PtacRoomLookup {
  return new Map(
    collectPtacRooms(structure).map((room) => [room.space.uniqueId, room])
  );
}

export function ptacUnitName(space: Pick<SpaceType, 'label'>): string {
  return `PTAC – ${space.label}`;
}

export function ptacUnitLoad(unit: CondenserType): {
  heating: number;
  cooling: number;
} {
  return ptacUnitZoneItems(unit).reduce(
    (total, item) => ({
      heating: total.heating + (item.BTUs?.heating ?? 0),
      cooling: total.cooling + (item.BTUs?.cooling ?? 0)
    }),
    { heating: 0, cooling: 0 }
  );
}

function ptacZone(
  name: string,
  zoneItems: ZoneItemType[],
  uniqueId: string = nanoid(5)
): ZoneType {
  return { uniqueId, name, type: 'ductless', zoneItems, modified: false };
}

function zoneItemForRoom(
  room: PtacRoom,
  uniqueId: string = nanoid(5)
): ZoneItemType {
  return {
    uniqueId,
    structureItemId: room.space.uniqueId,
    name: room.space.label,
    BTUs: room.space.BTUs,
    originalBTUs: room.space.BTUs
  };
}

function levelContextLookup(
  structure: LevelType[],
  homeOrientation: ExposureDirectionType | undefined
): PtacLevelContextLookup {
  const cache = new Map<string, PtacLevelContext>();
  return (level) => {
    const cached = cache.get(level.uniqueId);
    if (cached) return cached;
    const spaces = getGridSpacesForLevel(
      structure,
      structure.findIndex((entry) => entry.uniqueId === level.uniqueId)
    );
    const context = ptacLevelContextFor(
      structure,
      level,
      spaces,
      homeOrientation
    );
    cache.set(level.uniqueId, context);
    return context;
  };
}

function roomGridSpace(
  room: PtacRoom,
  level: PtacLevelContext
): GridSpaceDataType | null {
  return (
    level.spaces.find((space) => space.spaceId === room.space.uniqueId) ?? null
  );
}

function placementForRoom(
  room: PtacRoom,
  level: PtacLevelContext
): PositionType {
  const gridSpace = roomGridSpace(room, level);
  if (!gridSpace) {
    return {
      x: room.space.position?.x ?? 0,
      y: room.space.position?.y ?? 0
    };
  }
  return defaultPtacUnitPlacement(gridSpace, level).position;
}

function snappedPlacementForRoom(
  room: PtacRoom,
  level: PtacLevelContext,
  target: PositionType
): PositionType {
  const gridSpace = roomGridSpace(room, level);
  return gridSpace ? snapPtacUnitToRoom(gridSpace, level, target) : target;
}

export function ptacZoneItemsWithHost(items: ZoneItemType[]): ZoneItemType[] {
  const host = ptacHostItem(items);
  return items.map((item) =>
    item === host
      ? { ...item, servePassively: undefined }
      : { ...item, servePassively: true }
  );
}

function unitShell(ids: PtacUnitIds): Omit<CondenserType, 'zones' | 'name'> {
  return {
    uniqueId: ids.condenserUniqueId ?? nanoid(5),
    type: 'ductless',
    placementType: 'wall',
    heightOffGround: 0,
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    modified: false
  };
}

export function buildEmptyPtacUnit(
  zoneName: string,
  ids: PtacUnitIds = {}
): CondenserType {
  return {
    ...unitShell(ids),
    name: PTAC_EMPTY_UNIT_NAME,
    zones: [ptacZone(zoneName, [], ids.zoneUniqueId)]
  };
}

export function ptacUnitWithRooms(
  unit: CondenserType,
  items: ZoneItemType[],
  rooms: PtacRoomLookup,
  structure: LevelType[],
  previousHostRoomId: string | null = ptacUnitRoomId(unit)
): CondenserType {
  const zone = ptacUnitZone(unit) ?? ptacZone('Zone 1', []);
  const zoneItems = ptacZoneItemsWithHost(items);
  const host = ptacHostItem(zoneItems);
  const room = host ? rooms.get(host.structureItemId) : undefined;
  if (!host || !room) {
    return {
      ...unit,
      name: PTAC_EMPTY_UNIT_NAME,
      levelUniqueId: undefined,
      position: undefined,
      zones: [{ ...zone, zoneItems }]
    };
  }
  const hostChanged = previousHostRoomId !== host.structureItemId;
  return {
    ...unit,
    name: ptacUnitName(room.space),
    levelUniqueId: room.level.uniqueId,
    position: hostChanged ? undefined : unit.position,
    placementType: 'wall',
    heightOffGround: calculateHeightOffGround(
      'wall',
      room.level.uniqueId,
      structure
    ),
    zones: [{ ...zone, zoneItems }]
  };
}

export function rederivePtacUnits(
  previous: ConfigurationType | null,
  next: ConfigurationType,
  structure: LevelType[]
): ConfigurationType {
  const rooms = ptacRoomLookup(structure);
  return {
    ...next,
    condensers: next.condensers.map((unit) => {
      const before =
        previous?.condensers.find(
          (entry) => entry.uniqueId === unit.uniqueId
        ) ?? null;
      return ptacUnitWithRooms(
        unit,
        ptacUnitZoneItems(unit),
        rooms,
        structure,
        before ? ptacUnitRoomId(before) : null
      );
    })
  };
}

export function buildPtacUnit(
  room: PtacRoom,
  structure: LevelType[],
  level: PtacLevelContext,
  zoneName: string,
  ids: PtacUnitIds = {}
): CondenserType {
  return {
    ...unitShell(ids),
    name: ptacUnitName(room.space),
    levelUniqueId: room.level.uniqueId,
    position: placementForRoom(room, level),
    heightOffGround: calculateHeightOffGround(
      'wall',
      room.level.uniqueId,
      structure
    ),
    zones: [
      ptacZone(
        zoneName,
        [zoneItemForRoom(room, ids.zoneItemUniqueId)],
        ids.zoneUniqueId
      )
    ]
  };
}

function refreshedZoneItems(
  zone: ZoneType,
  rooms: PtacRoomLookup
): ZoneItemType[] {
  return zone.zoneItems.flatMap((item) => {
    const room = rooms.get(item.structureItemId);
    if (!room) return [];
    return [
      {
        ...item,
        name: room.space.label,
        BTUs: room.space.BTUs,
        originalBTUs: room.space.BTUs
      }
    ];
  });
}

function refreshedUnit(
  unit: CondenserType,
  rooms: PtacRoomLookup,
  structure: LevelType[],
  levelContextFor: PtacLevelContextLookup
): CondenserType {
  const zone = ptacUnitZone(unit);
  const items = zone ? refreshedZoneItems(zone, rooms) : [];
  const refreshed = ptacUnitWithRooms(unit, items, rooms, structure);
  const roomId = ptacUnitRoomId(refreshed);
  if (!roomId || refreshed.position === undefined) return refreshed;
  const room = rooms.get(roomId);
  if (!room) return refreshed;
  return {
    ...refreshed,
    position: snappedPlacementForRoom(
      room,
      levelContextFor(room.level),
      refreshed.position
    )
  };
}

export function newPtacConfiguration(
  name: string,
  aiGenerated = false
): ConfigurationType {
  return {
    uniqueId: nanoid(5),
    name,
    type: 'ductless',
    condensers: [],
    lineSets: [],
    valid: true,
    modified: false,
    aiGenerated
  };
}

export function newCustomPtacConfiguration(name: string): ConfigurationType {
  return {
    ...newPtacConfiguration(name),
    condensers: [buildEmptyPtacUnit('Zone 1')]
  };
}

export function refreshPtacConfiguration({
  current,
  structure,
  homeOrientation
}: PtacConfigurationSyncParams): ConfigurationType {
  const rooms = ptacRoomLookup(structure);
  const levelContextFor = levelContextLookup(structure, homeOrientation);
  return {
    ...current,
    condensers: current.condensers.map((unit) =>
      refreshedUnit(unit, rooms, structure, levelContextFor)
    )
  };
}

export function autoGeneratePtacConfiguration({
  current,
  structure,
  homeOrientation
}: PtacConfigurationSyncParams): ConfigurationType | null {
  const roomList = collectPtacRooms(structure);
  if (roomList.length === 0) return null;
  const rooms = ptacRoomLookup(structure);
  const levelContextFor = levelContextLookup(structure, homeOrientation);
  const refreshed = refreshPtacConfiguration({
    current,
    structure,
    homeOrientation
  });

  const keptUnits = refreshed.condensers
    .filter((unit) => ptacUnitZoneItems(unit).length > 0)
    .map((unit) => {
      const roomId = ptacUnitRoomId(unit);
      if (!roomId || unit.position !== undefined) return unit;
      const room = rooms.get(roomId);
      if (!room) return unit;
      return {
        ...unit,
        position: placementForRoom(room, levelContextFor(room.level))
      };
    });
  const covered = new Set(
    keptUnits.flatMap((unit) =>
      ptacUnitZoneItems(unit).map((item) => item.structureItemId)
    )
  );
  const newUnits = roomList
    .filter((room) => !covered.has(room.space.uniqueId))
    .map((room, index) =>
      buildPtacUnit(
        room,
        structure,
        levelContextFor(room.level),
        `Zone ${keptUnits.length + index + 1}`
      )
    );

  return renumberZones({
    ...refreshed,
    condensers: [...keptUnits, ...newUnits]
  });
}

export function placePtacUnitOnRoom(
  configuration: ConfigurationType,
  condenserUniqueId: string,
  room: PtacRoom,
  structure: LevelType[],
  level: PtacLevelContext,
  target?: PositionType
): ConfigurationType | null {
  const unit = configuration.condensers.find(
    (entry) => entry.uniqueId === condenserUniqueId
  );
  const zone = unit ? ptacUnitZone(unit) : null;
  if (!unit || !zone) return null;
  const roomIndex = zone.zoneItems.findIndex(
    (item) => item.structureItemId === room.space.uniqueId
  );
  if (roomIndex === -1) return null;

  const items = zone.zoneItems.map((item, index) => ({
    ...item,
    servePassively: index === roomIndex ? undefined : true
  }));
  const hosted = ptacUnitWithRooms(
    unit,
    items,
    new Map([[room.space.uniqueId, room]]),
    structure
  );
  const placed: CondenserType = {
    ...hosted,
    position: target
      ? snappedPlacementForRoom(room, level, target)
      : placementForRoom(room, level)
  };
  return {
    ...configuration,
    condensers: configuration.condensers.map((entry) =>
      entry.uniqueId === condenserUniqueId ? placed : entry
    )
  };
}

export function unplacePtacUnit(
  configuration: ConfigurationType,
  condenserUniqueId: string
): ConfigurationType {
  return {
    ...configuration,
    condensers: configuration.condensers.map((unit) =>
      unit.uniqueId === condenserUniqueId
        ? { ...unit, position: undefined }
        : unit
    )
  };
}

// The parts of the design the reconcile decides: which unit covers which
// rooms, and where the units sit. Fetched results and other runtime-only
// fields never trigger a rewrite.
export function ptacConfigurationSignature(
  configuration: ConfigurationType
): string {
  return JSON.stringify(
    configuration.condensers.map((unit) => ({
      uniqueId: unit.uniqueId,
      name: unit.name,
      levelUniqueId: unit.levelUniqueId ?? null,
      position: unit.position ?? null,
      placementType: unit.placementType ?? null,
      items: ptacUnitZoneItems(unit).map((item) => ({
        uniqueId: item.uniqueId,
        structureItemId: item.structureItemId,
        name: item.name,
        BTUs: item.BTUs,
        servePassively: item.servePassively === true
      }))
    }))
  );
}
